import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { Operator } from '../types';

interface AuthContextType {
  operator: Operator | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (operator: Operator) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [operator, setOperator] = useState<Operator | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Una sesión sin token NO es una sesión.
    //
    // Antes bastaba con tener el `operator` guardado. Eso funcionó mientras las
    // rutas del acta eran públicas: la app entraba, no mandaba cabecera y la API
    // respondía igual. Desde que exigen token, ese mismo usuario entra a una
    // pantalla que solo puede fallar — y como la API contesta 403, ni siquiera lo
    // rebotamos a login. Es lo que dejó a residentes sin poder abrir su acta.
    //
    // Se descarta la sesión a medias acá, que es donde se puede arreglar sola:
    // cae en /login, vuelve a entrar y sale con token.
    const storedOperator = localStorage.getItem('operator');
    const storedToken = localStorage.getItem('auth_token');

    if (storedOperator && !storedToken) {
      console.warn('Sesión sin token: se descarta y se pide login de nuevo');
      localStorage.removeItem('operator');
    } else if (storedOperator) {
      try {
        const parsedOperator = JSON.parse(storedOperator);
        setOperator(parsedOperator);
      } catch (error) {
        console.error('Error parsing stored operator:', error);
        localStorage.removeItem('operator');
      }
    }
    setIsLoading(false);
  }, []);

  const login = (newOperator: Operator) => {
    setOperator(newOperator);
    localStorage.setItem('operator', JSON.stringify(newOperator));
  };

  const logout = () => {
    setOperator(null);
    localStorage.removeItem('operator');
    localStorage.removeItem('auth_token');
  };

  const value: AuthContextType = {
    operator,
    isAuthenticated: !!operator,
    isLoading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
